import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button, Dialog, DialogContent } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useOnSaved } from '../../../hooks/useOnSaved';
import { useTranslation } from 'react-i18next';
import * as clientSlice from '../../../data/client.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import TextInput from '../../forms/TextInput';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { Client } from '../../../data/models/client.model';
import {
  Program,
  ServicePlan,
} from '../../../data/models/client-program.model';
import clientUtils from '../../../services/clientUtils';
import ChipSelectMenu from '../../forms/ChipSelectMenu';
import SelectMenu from '../../forms/SelectMenu';
import DateInput from '../../forms/DateInput';
import { TripLegStatus } from '../../../data/models/trip-leg-status.model';
import FullScreenDialogHeader from './FullScreenDialogHeader';

const PREFIX = 'ServicePlanDialog';

const classes = {
  content: `${PREFIX}-content`,
  formWrapper: `${PREFIX}-formWrapper`,
  formContainer: `${PREFIX}-formContainer`,
  formGroup: `${PREFIX}-formGroup`,
  fieldName: `${PREFIX}-fieldName`,
  fieldDescription: `${PREFIX}-fieldDescription`,
  fieldID: `${PREFIX}-fieldID`,
  fieldProgram: `${PREFIX}-fieldProgram`,
  dateContainer: `${PREFIX}-dateContainer`,
  dateField: `${PREFIX}-dateField`,
  chipField: `${PREFIX}-chipField`,
  lateCancelField: `${PREFIX}-lateCancelField`,
  regularField: `${PREFIX}-regularField`,
  spacer: `${PREFIX}-spacer`,
  actions: `${PREFIX}-actions`,
  addButton: `${PREFIX}-addButton`,
  cancelButton: `${PREFIX}-cancelButton`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    paddingLeft: theme.spacing(18),
    paddingRight: theme.spacing(18),
    paddingTop: 0,
    backgroundColor: theme.palette.background.default,
    height: 'fit-content',
  },

  [`& .${classes.formWrapper}`]: {
    flex: 1,
    display: 'flex',
    gap: theme.spacing(4),
    height: 'fit-content',
    paddingTop: theme.spacing(3),
  },

  [`& .${classes.formContainer}`]: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '3px',
    boxShadow: theme.shadows[2],
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: 'fit-content',
    height: 'fit-content',
    marginBottom: theme.spacing(8),
  },

  [`& .${classes.formGroup}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '500px',
  },

  [`& .${classes.fieldName}`]: {
    width: '394px',
  },

  [`& .${classes.fieldDescription}`]: {
    width: '394px',
  },

  [`& .${classes.fieldID}`]: {
    width: '197px',
  },

  [`& .${classes.fieldProgram}`]: {
    width: '394px',
  },

  [`& .${classes.dateContainer}`]: {
    display: 'flex',
    gap: theme.spacing(2),
  },

  [`& .${classes.dateField}`]: {
    width: '189px',
  },

  [`& .${classes.chipField}`]: {
    width: '100%',
  },

  [`& .${classes.lateCancelField}`]: {
    width: '222px',
  },

  [`& .${classes.regularField}`]: {
    width: '100%',
  },

  [`& .${classes.spacer}`]: {
    height: '16px',
  },

  [`& .${classes.actions}`]: {
    display: 'flex',
    gap: theme.spacing(3),
    paddingBottom: theme.spacing(1),
  },

  [`& .${classes.addButton}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    minWidth: '144px',
    height: '48px',
    fontSize: theme.typography.subtitle1.fontSize,
    letterSpacing: theme.typography.subtitle1.letterSpacing,
    fontWeight: theme.typography.subtitle1.fontWeight,
    fontFamily: theme.typography.subtitle1.fontFamily,
    textTransform: 'none',
    borderRadius: '3px',
    boxShadow: '0px 2px 7px 0px rgba(0, 0, 0, 0.3)',
    padding: '12px 22px',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: '0px 2px 7px 0px rgba(0, 0, 0, 0.3)',
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.lightActionDisabled12p.main,
      color: theme.palette.lightActionDisabled26p.main,
      boxShadow: 'none',
    },
  },

  [`& .${classes.cancelButton}`]: {
    minWidth: '144px',
    height: '48px',
    fontSize: '15px',
    letterSpacing: '0.46px',
    fontWeight: 600,
    fontFamily: 'Source Sans Pro',
    textTransform: 'none',
    borderRadius: '3px',
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
    padding: '12px 22px',
    '&:hover': {
      borderColor: theme.palette.primary.dark,
      backgroundColor: theme.palette.lightPrimaryShades04p.main,
    },
  },
}));

interface ServicePlanDialogProps {
  title: string;
  open: boolean;
  client: Client;
  programs: Program[];
  servicePlan?: ServicePlan;
  close: () => void;
}

export default function ServicePlanDialog({
  title,
  open,
  client,
  programs,
  servicePlan,
  close,
}: ServicePlanDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  const savingServicePlan = useAppSelector(clientSlice.selectSavingServicePlan);
  const savedServicePlan = useAppSelector(clientSlice.selectSavedServicePlan);

  const transportationBenefits = useAppSelector(
    clientSlice.selectTransportationBenefits,
  );

  const clientServiceLevels = useAppSelector(
    accountingSlice.selectTransportationServices,
  );

  const purposeCodes = useAppSelector(clientSlice.selectPurposeCodes);
  const serviceAreas = useAppSelector(clientSlice.selectServiceAreas);

  const availableStatuses = [
    { name: 'canceled', value: TripLegStatus.Canceled },
    { name: 'canceledlate', value: TripLegStatus.CanceledLate },
    { name: 'noload', value: TripLegStatus.NoLoad },
    { name: 'completed', value: TripLegStatus.Completed },
    { name: 'denied', value: TripLegStatus.Denied },
    { name: 'abandoned', value: TripLegStatus.Abandoned },
  ];

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: clientUtils.getServicePlanDefaults(servicePlan),
  });

  React.useEffect(() => {
    const defaults = clientUtils.getServicePlanDefaults(servicePlan);
    formHook.reset(defaults);
  }, [servicePlan]);

  const handleClose = () => {
    formHook.reset();
    close();
  };

  useOnSaved(savedServicePlan, handleClose);

  const handleSave = (data: any) => {
    const newServicePlan = clientUtils.getServicePlanFromFormData(
      client.clientBC,
      data,
      !!servicePlan?.programUUID,
    );
    if (servicePlan) {
      dispatch(clientSlice.updateClientServicePlanAsync(newServicePlan));
    } else {
      dispatch(clientSlice.createClientServicePlanAsync(newServicePlan));
    }
  };

  return (
    <RootDialog open={open} onClose={handleClose} fullScreen>
      <FullScreenDialogHeader
        clientName={client?.legalName || ''}
        title={title}
        close={handleClose}
      />
      <DialogContent className={classes.content}>
        <Box className={classes.formWrapper}>
          <Box className={classes.formContainer}>
            <Box className={classes.formGroup}>
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <TextInput
                  name="DisplayName"
                  label={t('client.servicePlanName')}
                  className={classes.fieldName}
                  control={formHook.control}
                  disabled={false}
                  required={true}
                  rules={{ required: requiredFieldMessage }}
                />
                <TextInput
                  name="PlanName"
                  label={t('client.description')}
                  className={classes.fieldDescription}
                  control={formHook.control}
                  disabled={false}
                  required={true}
                  rules={{ required: requiredFieldMessage }}
                />
                <TextInput
                  name="ClientID"
                  label={t('client.servicePlanId')}
                  className={classes.fieldID}
                  control={formHook.control}
                  disabled={false}
                  required={true}
                  rules={{ required: requiredFieldMessage }}
                />
                <SelectMenu
                  label={t('client.program')}
                  name="Program"
                  className={classes.fieldProgram}
                  values={programs}
                  valueAttr="programUUID"
                  labelAttr="name"
                  required={true}
                  disabled={!!servicePlan?.programUUID}
                  control={formHook.control}
                  rules={{ required: requiredFieldMessage }}
                />
              </LocalizationProvider>
            </Box>

            <ChipSelectMenu
              label={t('client.billableStatuses')}
              name="BillableStatuses"
              className={classes.chipField}
              values={availableStatuses}
              valueAttr="value"
              labelAttr="name"
              required={true}
              control={formHook.control}
              rules={{ required: requiredFieldMessage }}
            />
            <ChipSelectMenu
              label={t('client.reimbursableStatuses')}
              name="ReimbursableStatuses"
              className={classes.chipField}
              values={availableStatuses}
              valueAttr="value"
              labelAttr="name"
              required={true}
              control={formHook.control}
              rules={{ required: requiredFieldMessage }}
            />
            <TextInput
              name="LateCancelDeltaMinutes"
              label={t('client.lateCancelDeltaMinutes')}
              className={classes.lateCancelField}
              control={formHook.control}
              disabled={false}
              required={true}
              rules={{
                min: 0,
                max: 10080,
                required: requiredFieldMessage,
              }}
              inputProps={{ max: 10080, min: 0, type: 'number' }}
            />

            <Box className={classes.spacer} />

            <Box className={classes.actions}>
              <Button
                variant="contained"
                color="primary"
                className={classes.addButton}
                disabled={!formHook.formState.isValid || savingServicePlan}
                onClick={formHook.handleSubmit(handleSave)}>
                {t('common.add')}
              </Button>
              <Button
                variant="outlined"
                className={classes.cancelButton}
                onClick={handleClose}>
                {t('common.cancel')}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </RootDialog>
  );
}
